import { CodexManager } from './CodexManager';
import { ShopManager, Wallet } from './ShopManager';
import { ProgressManager, PlayerLevel, LoginStreak } from './ProgressManager';
import { Tier, STARTING_FAMILIES } from '../config';

/**
 * Full save data structure (version 2).
 */
export interface SaveData {
  version: number;
  timestamp: number;
  codex: {
    discoveries: string[];
  };
  shop: {
    wallet: Wallet;
    purchaseCounts: Record<string, number>;
  };
  progress: {
    level: PlayerLevel;
    loginStreak: LoginStreak;
    stats: {
      totalMerges: number;
      totalSplinters: number;
      totalScore: number;
      highestTier: Tier;
      totalPlayTimeMs: number;
    };
    achievements: Record<string, { unlocked: boolean; progress: number }>;
  };
  game: {
    score: number;
    highScore: number;
    runsCompleted: number;
    unlockedFamilies: string[];
    activeFamilies?: string[];
    activeModifiers?: string[];
    modifierState?: { splinterUsedThisRun?: boolean };
    gridState: Array<{ uid: number; defId: string; col: number; row: number; age?: number; corrupted?: boolean }>;
  };
}

/**
 * Outcome of attempting to read a persisted save.
 *
 * `missing`   — no save present; treat as a brand-new player.
 * `parse`     — the stored bytes could not be parsed or failed structural validation.
 *               The caller MUST surface this to the player (see issue #25): a silent
 *               fallback to a fresh run destroys their progress without warning.
 * `version`   — the stored save is newer than this build supports (forward-compat guard).
 */
export type LoadResult =
  | { ok: true; data: SaveData }
  | { ok: false; reason: 'missing' | 'parse' | 'version'; raw?: string; error?: unknown };

/**
 * Outcome of attempting to persist a save. `quota` means the browser rejected
 * the write because localStorage is full; `unknown` covers everything else
 * (e.g. storage disabled in private mode). Either way the caller should warn
 * the player so they can act before the next background/crash loses state.
 */
export type WriteResult =
  | { ok: true }
  | { ok: false; reason: 'quota' | 'unknown'; error?: unknown };

/**
 * SaveManager coordinates saving and loading across all game managers.
 * In production, saves go to Supabase cloud; for now, we serialize to JSON.
 */
export class SaveManager {
  private static readonly SAVE_VERSION = 3;
  static readonly LOCAL_SAVE_KEY = 'splintr_save';

  /**
   * Create a full save snapshot from all managers.
   */
  static createSaveData(
    codex: CodexManager,
    shop: ShopManager,
    progress: ProgressManager,
    gameState: {
      score: number;
      highScore: number;
      runsCompleted: number;
      unlockedFamilies: string[];
      activeFamilies?: string[];
      activeModifiers?: string[];
      modifierState?: { splinterUsedThisRun?: boolean };
      gridState: Array<{ uid: number; defId: string; col: number; row: number; age?: number; corrupted?: boolean }>;
    }
  ): SaveData {
    return {
      version: SaveManager.SAVE_VERSION,
      timestamp: Date.now(),
      codex: {
        discoveries: codex.exportDiscoveries(),
      },
      shop: shop.exportState(),
      progress: progress.exportState(),
      game: gameState,
    };
  }

  /**
   * Restore all managers from save data.
   */
  static loadSaveData(
    data: SaveData,
    codex: CodexManager,
    shop: ShopManager,
    progress: ProgressManager
  ): {
    score: number;
    highScore: number;
    runsCompleted: number;
    unlockedFamilies: string[];
    activeFamilies?: string[];
    activeModifiers?: string[];
    modifierState?: { splinterUsedThisRun?: boolean };
    gridState: Array<{ uid: number; defId: string; col: number; row: number; age?: number; corrupted?: boolean }>;
  } {
    // Validate version
    if (data.version > SaveManager.SAVE_VERSION) {
      throw new Error(`Save version ${data.version} is newer than supported version ${SaveManager.SAVE_VERSION}`);
    }

    // Migrate v1 -> v2: add new game fields
    if (data.version < 2) {
      const defaultFamilies = STARTING_FAMILIES.map(f => f as string);
      data.game = {
        ...data.game,
        highScore: data.game.highScore ?? data.game.score ?? 0,
        runsCompleted: data.game.runsCompleted ?? 0,
        unlockedFamilies: data.game.unlockedFamilies ?? defaultFamilies,
      };
      data.version = 2;
    }

    // Migrate v2 -> v3: derive activeFamilies from unlockedFamilies. Old saves
    // become eligible for the draft on next run start.
    if (data.version < 3) {
      data.game = {
        ...data.game,
        activeFamilies: data.game.activeFamilies ?? data.game.unlockedFamilies,
      };
      data.version = 3;
    }

    // Restore codex
    codex.loadDiscoveries(data.codex.discoveries);

    // Restore shop
    shop.loadState(data.shop);

    // Restore progress
    progress.loadState(data.progress);

    // Return game state for the scene to apply
    return data.game;
  }

  /**
   * Serialize save data to JSON string.
   */
  static serialize(data: SaveData): string {
    return JSON.stringify(data);
  }

  /**
   * Deserialize JSON string to save data.
   */
  static deserialize(json: string): SaveData {
    const data = JSON.parse(json);

    // Basic validation
    if (typeof data.version !== 'number') {
      throw new Error('Invalid save data: missing version');
    }
    if (typeof data.timestamp !== 'number') {
      throw new Error('Invalid save data: missing timestamp');
    }
    if (!data.codex || !data.shop || !data.progress || !data.game) {
      throw new Error('Invalid save data: missing required sections');
    }

    return data as SaveData;
  }

  /**
   * Validate save data integrity.
   */
  static isValid(data: unknown): data is SaveData {
    if (typeof data !== 'object' || data === null) return false;
    const d = data as Record<string, unknown>;

    return (
      typeof d.version === 'number' &&
      typeof d.timestamp === 'number' &&
      typeof d.codex === 'object' &&
      typeof d.shop === 'object' &&
      typeof d.progress === 'object' &&
      typeof d.game === 'object'
    );
  }

  /**
   * Read and parse the save from localStorage without throwing. Returns a
   * discriminated result so the caller can react to each failure mode
   * separately — in particular, `parse` must never be silently mapped to
   * "start a fresh run" (see issue #25).
   */
  static readLocal(storage: Pick<Storage, 'getItem'> = localStorage): LoadResult {
    let raw: string | null;
    try {
      raw = storage.getItem(SaveManager.LOCAL_SAVE_KEY);
    } catch (error) {
      return { ok: false, reason: 'parse', error };
    }
    if (raw === null) return { ok: false, reason: 'missing' };

    try {
      const data = SaveManager.deserialize(raw);
      if (data.version > SaveManager.SAVE_VERSION) {
        return { ok: false, reason: 'version', raw };
      }
      return { ok: true, data };
    } catch (error) {
      return { ok: false, reason: 'parse', raw, error };
    }
  }

  /**
   * Serialize and persist a save to localStorage. Quota-exceeded errors are
   * caught and returned so the caller can surface a warning instead of
   * letting the write fail silently (see issue #25).
   */
  static writeLocal(
    data: SaveData,
    storage: Pick<Storage, 'setItem'> = localStorage
  ): WriteResult {
    const json = SaveManager.serialize(data);
    try {
      storage.setItem(SaveManager.LOCAL_SAVE_KEY, json);
      return { ok: true };
    } catch (error) {
      if (SaveManager.isQuotaExceeded(error)) {
        return { ok: false, reason: 'quota', error };
      }
      return { ok: false, reason: 'unknown', error };
    }
  }

  /**
   * Cross-browser check for the QuotaExceededError thrown by localStorage
   * when the 5–10 MB budget is full. Firefox uses code 1014, WebKit 22,
   * Chromium exposes only the name.
   */
  private static isQuotaExceeded(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    if (error.name === 'QuotaExceededError') return true;
    if (error.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
    const code = (error as { code?: number }).code;
    return code === 22 || code === 1014;
  }

  /**
   * Get the age of a save in milliseconds.
   */
  static getSaveAge(data: SaveData): number {
    return Date.now() - data.timestamp;
  }

  /**
   * Format save age as human-readable string.
   */
  static formatSaveAge(data: SaveData): string {
    const ageMs = SaveManager.getSaveAge(data);
    const seconds = Math.floor(ageMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'just now';
  }
}
